import { Plane, Loader2, ChevronLeft, Check, SkipForward } from 'lucide-react';
import { getCityName } from '../../utils/airports';
import { hapticMedium } from '../../utils/haptics';
import type { ParsedRosterEntry, CandidateFlight } from '../../types';

interface FlightMatchStepProps {
  entries: ParsedRosterEntry[];
  candidates: Map<number, CandidateFlight[]>;
  selections: Map<number, string | null>;
  loading: boolean;
  onSelect: (entryIndex: number, candidateId: string | null) => void;
  onConfirm: () => void;
  onBack: () => void;
}

export function FlightMatchStep({
  entries,
  candidates,
  selections,
  loading,
  onSelect,
  onConfirm,
  onBack,
}: FlightMatchStepProps) {
  // Only show unmatched flight entries
  const unmatchedLegs = entries
    .map((e, i) => ({ entry: e, index: i }))
    .filter(({ entry }) => entry.type === 'flight' && !entry.flightNumber && entry.departureAirport && entry.arrivalAirport);

  const allResolved = unmatchedLegs.every(({ index }) => {
    const cands = candidates.get(index);
    // Resolved if: user picked a candidate, explicitly skipped, or no candidates available
    return selections.get(index) !== undefined || !cands || cands.length === 0;
  });

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-slate-400">
        {unmatchedLegs.length} leg{unmatchedLegs.length !== 1 ? 's' : ''} without flight numbers.
        Select the matching flight for each.
      </p>
      <p className="text-[10px] text-slate-500 -mt-1">
        Times are approximate and may not reflect real-time changes.
      </p>

      <div
        className="flex flex-col gap-3 max-h-[50vh] overflow-y-auto"
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
        }}
      >
        {unmatchedLegs.map(({ entry, index }) => (
          <LegCard
            key={index}
            entry={entry}
            index={index}
            candidates={candidates.get(index) || []}
            selectedId={selections.get(index) ?? undefined}
            loading={loading}
            onSelect={onSelect}
          />
        ))}
      </div>

      <div className="flex gap-2 mt-1">
        <button
          onClick={() => { hapticMedium(); onConfirm(); }}
          disabled={loading || !allResolved}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-dark disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <Check size={16} />
              Confirm & Review
            </>
          )}
        </button>
        <button
          onClick={onBack}
          disabled={loading}
          className="flex items-center gap-1 px-4 py-2.5 text-sm font-medium rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
        >
          <ChevronLeft size={14} />
          Back
        </button>
      </div>
    </div>
  );
}

function LegCard({
  entry,
  index,
  candidates,
  selectedId,
  loading,
  onSelect,
}: {
  entry: ParsedRosterEntry;
  index: number;
  candidates: CandidateFlight[];
  selectedId: string | undefined;
  loading: boolean;
  onSelect: (entryIndex: number, candidateId: string | null) => void;
}) {
  const isSkipped = selectedId === null && selectedId !== undefined;
  const depCity = entry.departureAirport ? getCityName(entry.departureAirport) : '';

  return (
    <div className="bg-slate-700/50 border border-slate-600/50 rounded-xl p-3">
      {/* Route header */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <Plane size={14} className="text-accent" />
          <span className="text-sm font-semibold text-slate-100 font-mono">
            {entry.departureAirport} → {entry.arrivalAirport}
          </span>
          {depCity !== entry.departureAirport && (
            <span className="text-[10px] text-slate-500">{depCity}</span>
          )}
        </div>
        <span className="text-[10px] font-mono text-slate-500 bg-slate-600/50 px-1.5 py-0.5 rounded">
          {entry.date}
        </span>
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="flex flex-col gap-1.5">
          {[1, 2, 3].map((n) => (
            <div key={n} className="h-10 rounded-lg bg-slate-600/30 animate-pulse" />
          ))}
        </div>
      )}

      {/* No candidates */}
      {!loading && candidates.length === 0 && (
        <div className="text-xs text-slate-500 italic py-2">
          No schedule data available — will import as route only.
        </div>
      )}

      {/* Candidate list */}
      {!loading && candidates.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {candidates.map((c) => {
            const isSelected = selectedId === c.id;
            const isOnly = candidates.length === 1;
            return (
              <button
                key={c.id}
                onClick={() => onSelect(index, c.id)}
                className={`flex items-center gap-2.5 p-2 rounded-lg text-left transition-colors ${
                  isSelected
                    ? isOnly
                      ? 'border border-emerald-500/30 bg-emerald-500/[0.03]'
                      : 'border border-accent/30 bg-accent/[0.06]'
                    : 'border border-slate-600/30 hover:bg-slate-600/30'
                }`}
              >
                {/* Radio circle */}
                <div
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    isSelected ? 'bg-accent border-accent' : 'border-slate-500'
                  }`}
                >
                  {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                </div>

                {/* Flight info */}
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <span className="text-sm font-bold font-mono text-slate-100">
                    {c.flightNumber}
                  </span>
                  {(c.departureTime || c.arrivalTime) && (
                    <span className="text-xs font-mono text-slate-400">
                      {c.departureTime || '?'} → {c.arrivalTime || '?'}
                    </span>
                  )}
                </div>

                {/* Auto-matched badge */}
                {isOnly && isSelected && (
                  <span className="text-[9px] font-medium text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded shrink-0">
                    Only flight
                  </span>
                )}
              </button>
            );
          })}

          {/* Skip option */}
          <button
            onClick={() => onSelect(index, null)}
            className={`flex items-center gap-2.5 p-2 rounded-lg text-left transition-colors border border-dashed ${
              isSkipped
                ? 'border-slate-500 bg-slate-600/20'
                : 'border-slate-600/30 hover:bg-slate-600/20'
            }`}
          >
            <div
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                isSkipped ? 'bg-slate-500 border-slate-500' : 'border-slate-500'
              }`}
            >
              {isSkipped && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
            </div>
            <SkipForward size={12} className="text-slate-500" />
            <span className="text-xs text-slate-500">Skip — import without details</span>
          </button>
        </div>
      )}
    </div>
  );
}
